import React, { useState } from 'react';
import { ConfirmDialog } from '@/components';
import {
  showErrorToast,
  showSuccessToast,
} from '@/components/toast/globalToast';
import { PackageDetail, PackageStatus } from '@/types/package';
import { getPackageById, updatePackage } from '@/services/api/packages';
import { extractApiErrorMessage } from '@/utils';
import { fromPackageDetail, toPackageWriteBody } from './packageForm';

/**
 * Sets ACTIVE/INACTIVE on the stored package. The API only takes a full PUT,
 * so the stored detail is re-read and written back with just the status
 * changed — the same body an untouched "Simpan Perubahan" would send.
 */
export const setPackageStatus = async (
  id: string,
  status: PackageStatus.ACTIVE | PackageStatus.INACTIVE
): Promise<PackageDetail> => {
  const detail = await getPackageById(id);
  return updatePackage(id, {
    ...toPackageWriteBody(fromPackageDetail(detail), detail),
    status,
  });
};

/**
 * What the status action does to a package currently in `status`, or null for
 * ARCHIVED — that one comes back only through Unarchive.
 */
export const packageStatusAction = (status: PackageStatus) => {
  if (status === PackageStatus.ARCHIVED) return null;
  return status === PackageStatus.ACTIVE
    ? {
        next: PackageStatus.INACTIVE as const,
        label: 'Nonaktifkan',
        icon: 'ri-pause-circle-line',
        title: 'Nonaktifkan package ini?',
        description:
          'Package berhenti dicocokkan ke unit baru dan hilang dari aplikasi mitra. Sewa yang sedang berjalan tidak terpengaruh.',
        success: 'Package dinonaktifkan',
      }
    : {
        next: PackageStatus.ACTIVE as const,
        label: 'Aktifkan',
        icon: 'ri-play-circle-line',
        title: 'Aktifkan package ini?',
        description:
          'Package mulai dicocokkan ke unit dan muncul di aplikasi mitra.',
        success: 'Package diaktifkan',
      };
};

/**
 * Confirm-then-flip flow shared by the edit header and the list row menu:
 * `request` opens the dialog, `dialog` renders it, `onChanged` gets the
 * package as saved.
 */
export const usePackageStatusDialog = (
  onChanged: (detail: PackageDetail) => void
) => {
  const [target, setTarget] = useState<{
    id: string;
    status: PackageStatus;
  } | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const action = target && packageStatusAction(target.status);

  const handleConfirm = async () => {
    if (!target || !action) return;
    setIsBusy(true);
    try {
      onChanged(await setPackageStatus(target.id, action.next));
      showSuccessToast('Success', action.success);
    } catch (error) {
      showErrorToast(
        'Error',
        extractApiErrorMessage(error, 'Failed to change package status')
      );
    } finally {
      setIsBusy(false);
      setTarget(null);
    }
  };

  const dialog = (
    <ConfirmDialog
      isOpen={!!action}
      title={action?.title ?? ''}
      description={action?.description}
      confirmLabel={isBusy ? 'Menyimpan...' : (action?.label ?? '')}
      cancelLabel="Batal"
      onConfirm={handleConfirm}
      onCancel={() => setTarget(null)}
      isBusy={isBusy}
    />
  );

  return {
    request: (id: string, status: PackageStatus) => setTarget({ id, status }),
    isBusy,
    dialog,
  };
};
